import { mapActions } from 'vuex';
import { ToastProgrammatic as Toast } from 'buefy';
import Draggable from 'vuedraggable';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

import {
  required, requiredIf, minValue, maxValue,
} from '@/utils/validators';
import {
  GET_DEPARTMENT_LIST,
  UPDATE_DEPARTMENT_INFO,
  GET_TEAM_INFO,
  UPDATE_TEAM_INFO,
} from '@/store/setting/constants';

export default {
  name: 'SettingOverview',
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    Draggable,
  },
  data() {
    return {
      identification: '',
      loading: false,
      teamInfo: {
        name: '',
        enablePickupCode: false,
        autoCompleteGroupBuying: false,
        autoCompleteDays: null,
      },
      departmentInfo: {
        departments: [],
        deleted_ids: [],
      },
      planName: '',
    };
  },
  computed: {
    departments() {
      return this.departmentInfo?.departments || [];
    },
  },
  async mounted() {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.loading = true;
      const {
        name,
        departments,
        enable_pickup_code: enablePickupCode,
        identification,
        plan_name: planName = '',
        auto_complete_group_buying: autoCompleteGroupBuying = false,
        auto_complete_days: autoCompleteDays = null,
      } = await this.getTeamInfo();

      this.teamInfo = {
        name, enablePickupCode, autoCompleteGroupBuying, autoCompleteDays,
      };
      this.departmentInfo.departments = departments;
      this.identification = identification;
      this.planName = planName;
    } catch (ex) {
      const errorMessage = ex.message ? `(${ex.message})` : '';

      Toast.open({
        type: 'is-danger',
        message: `無法讀取帳戶資訊，請稍後再試${errorMessage}`,
      });
    } finally {
      this.loading = false;
    }
  },
  watch: {
    'teamInfo.autoCompleteGroupBuying': {
      handler(val) {
        if (val && !this.teamInfo.autoCompleteDays) {
          this.teamInfo.autoCompleteDays = 1;
        } else if (!val) {
          this.teamInfo.autoCompleteDays = null;
        }
      },
    },
  },
  methods: {
    async onCreateDepartment() {
      this.departmentInfo.departments.push({ name: '' });
      this.v$.departmentInfo.$touch();
    },
    async onRemoveDepartment(index) {
      const { departmentInfo } = this;
      const targetId = departmentInfo.departments[index].id;

      departmentInfo.departments.splice(index, 1);
      this.v$.departmentInfo.$touch();
      if (targetId) {
        departmentInfo.deleted_ids = departmentInfo.deleted_ids || [];
        departmentInfo.deleted_ids.push(targetId);
      }
    },
    async onSubmitDepartment() {
      try {
        this.loading = true;

        /* Remove empty field */
        this.departmentInfo.departments = this.departmentInfo.departments.filter((d) => d.name);

        await this.updateDepartmentInfo(this.departmentInfo);

        const { departments } = await this.getTeamInfo();
        this.departmentInfo.departments = departments;
        this.departmentInfo.deleted_ids = [];

        this.v$.departmentInfo.$reset();
        Toast.open({
          type: 'is-success',
          message: '部門資訊已更新',
        });
      } catch (ex) {
        const errorMessage = ex.message ? `(${ex.message})` : '';

        Toast.open({
          type: 'is-danger',
          message: `無法更新部門資訊，請稍後再試${errorMessage}`,
        });
      } finally {
        this.loading = false;
      }
    },
    async onSubmitTeamInfo() {
      try {
        this.loading = true;
        const {
          name, enablePickupCode, autoCompleteGroupBuying, autoCompleteDays,
        } = this.teamInfo;
        await this.updateTeamInfo({
          name,
          enable_pickup_code: enablePickupCode,
          auto_complete_group_buying: autoCompleteGroupBuying,
          auto_complete_days: autoCompleteGroupBuying ? Number(autoCompleteDays) : null,
        });
        Toast.open({
          type: 'is-success',
          message: '公司資訊已更新',
        });
      } catch (ex) {
        const errorMessage = ex.message ? `(${ex.message})` : '';

        Toast.open({
          type: 'is-danger',
          message: `無法更新公司資訊，請稍後再試${errorMessage}`,
        });
      } finally {
        this.v$.teamInfo.$reset();
        this.loading = false;
      }
    },
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
      updateDepartmentInfo: UPDATE_DEPARTMENT_INFO,
      getTeamInfo: GET_TEAM_INFO,
      updateTeamInfo: UPDATE_TEAM_INFO,
    }),
  },
  validations() {
    return {
      teamInfo: {
        name: { required: required('公司名稱') },
        enablePickupCode: {},
        autoCompleteGroupBuying: {},
        autoCompleteDays: {
          requiredIf: requiredIf('自動完成天數', () => this.teamInfo.autoCompleteGroupBuying),
          minValue: minValue('自動完成天數', 1),
          maxValue: maxValue('自動完成天數', 30),
        },
      },
      departmentInfo: {
        departments: {
          $each: helpers.forEach({
            name: {
              required: required('部門名稱'),
            },
          }),
        },
      },
    };
  },
};
